/**
 * O que faz: escolha explícita da coluna certa para um contato ("Corrigir sugestão"). Só envia depois de
 *            "Confirmar correção" e avisa que a organização automática daquele contato fica pausada.
 * Onde usa: ModalRevisaoAgentes (revisão das sugestões) e DialogCorrecaoOrganizacao (card do Pipeline).
 * Conexões: recebe as colunas e a ação prontas do dono; não chama o banco diretamente.
 * Tabelas: nenhuma direta (a ação usa a RPC corrigir_organizacao_lead).
 * Data: 21/10/2026.
 */
import { useId, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface CorrecaoOrganizacaoLeadProps {
  leadId: string;
  etapas: Array<{ id: string; nome: string }>;
  onConfirmar: (etapaId: string) => Promise<void>;
  // Coluna onde o card está hoje: aparece marcada como "atual" para a pessoa se orientar.
  etapaAtualId?: string | null;
}

export function CorrecaoOrganizacaoLead({ leadId, etapas, onConfirmar, etapaAtualId = null }: CorrecaoOrganizacaoLeadProps) {
  // IDs ligados ao lead: duas correções abertas na mesma tela não compartilham rótulos.
  const idBase = `${useId()}-${leadId}`;
  const [escolhida, setEscolhida] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const confirmar = async () => {
    // Validação: nada é enviado sem uma coluna escolhida; o servidor confere tenant e versão do card.
    if (!escolhida || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await onConfirmar(escolhida);
    } catch (falha) {
      // A escolha continua marcada para tentar de novo depois de atualizar o card.
      setErro(falha instanceof Error && falha.message ? falha.message : 'Não foi possível corrigir este card. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Quadros com muitas colunas: a própria lista rola, com scroll-fade no dono do scroll. */}
      <RadioGroup value={escolhida} onValueChange={setEscolhida} aria-label="Coluna certa para este contato" className="scroll-fade max-h-48 gap-2 overflow-y-auto pr-1">
        {etapas.map((etapa) => (
          <div key={etapa.id} className="flex items-center gap-2">
            <RadioGroupItem value={etapa.id} id={`${idBase}-${etapa.id}`} />
            <Label htmlFor={`${idBase}-${etapa.id}`} className="font-normal">{etapa.nome}</Label>
            {etapa.id === etapaAtualId && <span className="text-[11px] text-muted-foreground" aria-hidden="true">(atual)</span>}
          </div>
        ))}
      </RadioGroup>
      <p className="text-xs text-muted-foreground">
        Depois da correção, a organização automática deste contato fica pausada. As regras das colunas não mudam.
      </p>
      {erro && <p role="alert" className="text-xs lg-texto-perigo animate-in fade-in-0 duration-200 motion-reduce:animate-none">{erro}</p>}
      <Button type="button" size="sm" className="rounded-full" disabled={!escolhida || enviando} onClick={() => void confirmar()}>
        {enviando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="mr-1 h-4 w-4" aria-hidden="true" />}
        Confirmar correção
      </Button>
    </div>
  );
}
